<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <TableAlert v-if="items.length" />

        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t('Common.projects') }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex
              v-if="
                claims.company['ROLE_DELETE_COMPANY_COMPANYID_PROJECT_MULTIDELETE'] ||
                claims.project['ROLE_PUT_PROJECT_ID'] ||
                claims.project['ROLE_DELETE_PROJECT_ID']
              "
              class="table__checkbox"
              xs1
            >
              <v-checkbox
                v-if="
                  claims.company['ROLE_DELETE_COMPANY_COMPANYID_PROJECT_MULTIDELETE'] &&
                  items && items.length
                "
                v-model="checkedAllItems"
                :disabled="!selectedCompany"
                hide-details
                @change="handleCheckedAllItems"
              />
            </v-flex>
            <v-flex xs1>ID</v-flex>
            <v-flex xs4>{{ $t('Common.project') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs6>{{ $t('Common.company') | capitalizeFirstLetter }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex
                        v-if="
                          claims.company['ROLE_DELETE_COMPANY_COMPANYID_PROJECT_MULTIDELETE'] ||
                          claims.project['ROLE_PUT_PROJECT_ID'] ||
                          claims.project['ROLE_DELETE_PROJECT_ID']
                        "
                        class="table__checkbox table__td--prevent"
                        xs1
                      >
                        <v-checkbox
                          v-if="claims.company['ROLE_DELETE_COMPANY_COMPANYID_PROJECT_MULTIDELETE']"
                          v-model="selectedItems"
                          :value="item.id"
                          :disabled="!selectedCompany"
                          hide-details
                          @change="handleSelectedItems"
                        />

                        <v-menu
                          v-if="
                            claims.project['ROLE_PUT_PROJECT_ID'] ||
                            claims.project['ROLE_DELETE_PROJECT_ID']
                          "
                          offset-y
                          right
                        >
                          <button class="table__body__actions" slot="activator">
                            <v-icon>more_vertical</v-icon>
                          </button>

                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile
                              v-if="claims.project['ROLE_PUT_PROJECT_ID']"
                              @click="handleEditItem(item)"
                            >
                              {{ $t('Common.edit') }} {{ $t('Common.project') }}
                            </v-list-tile>

                            <v-divider v-if="claims.project['ROLE_PUT_PROJECT_ID'] && claims.project['ROLE_DELETE_PROJECT_ID']" />

                            <v-list-tile
                              v-if="claims.project['ROLE_DELETE_PROJECT_ID']"
                              @click="handleSingleDeleteDialog(item)"
                            >
                              {{ $t('Common.delete') }} {{ $t('Common.project') }}
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.id" class="text--ellipsis" contenteditable>
                          {{ item.id }}
                        </span>
                      </v-flex>

                      <v-flex xs4>
                        <span class="text--ellipsis" :title="item.name">
                          {{ item.name }}
                        </span>
                      </v-flex>

                      <v-flex xs6>
                        <span class="text--ellipsis" :title="item.company.name">
                          {{ item.company.name }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneFound`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :actionType="globalDrawer.action"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { formatNumber } from 'utils/functions'
import TableAlert from './TableAlert'
import EmptyState from 'components/EmptyState'
import GlobalDrawer from 'components/globalDrawer'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    clearSelectedItems: {
      type: Boolean
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    TableAlert,
    EmptyState,
    GlobalDrawer
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.ProjectsPage',
      selectedItem: null,
      checkedAllItems: false,
      selectedItems: [],
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {},
        action: null
      }
    }
  },
  methods: {
    openGlobalDrawer ({ data = {}, filters = {}, action = 'preview' }) {
      this.globalDrawer = {
        visibility: true,
        type: 'project',
        data,
        filters,
        action
      }
    },

    closeGlobalDrawer (reload = null) {
      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {},
        action: null
      }

      this.selectedItem = null
      reload && this.$emit('reloadData')
    },

    handleEditItem (item) {
      this.openGlobalDrawer({
        data: {
          id: item.id
        },
        action: 'edit'
      })

      this.selectedItem = item.id
    },

    handlePage (page) {
      this.closeGlobalDrawer()
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.id === this.selectedItem || this.selectedItems.find(i => i === item.id)
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.id
        this.openGlobalDrawer({
          data: {
            id: item.id,
            name: item.name
          }
        })
      }
    },

    openDrawerIfIdExist () {
      if (this.routeId) {
        this.openGlobalDrawer({
          data: {
            id: this.routeId
          }
        })
        this.selectedItem = this.routeId
      }
    },

    handleSingleDeleteDialog (item) {
      this.$emit('deleteItem', item)
    },

    handleCheckedAllItems (val) {
      this.selectedItems = val
        ? [...this.items].map(item => item.id)
        : []
    },

    handleSelectedItems (val) {
      this.checkedAllItems = this.selectedItems.length === this.items.length
    }
  },
  watch: {
    selectedCompany () {
      this.closeGlobalDrawer()
    },

    selectedItems (val) {
      this.$emit('selectedItems', val)
    },

    clearSelectedItems () {
      this.selectedItems = []
      this.checkedAllItems = false
    },

    drawerVisibility () {
      this.closeGlobalDrawer()
    }
  },
  mounted () {
    this.openDrawerIfIdExist()
  }
}
</script>
